import { getDefaultClient } from '@dynamic-labs-sdk/client';
import {
  MethodNotImplementedError,
  getWalletProviderFromWalletAccount,
} from '@dynamic-labs-sdk/client/core';

import type { BitcoinWalletAccount } from '../BitcoinWalletAccount.types';
import type {
  BitcoinAddressType,
  BitcoinSignProtocol,
} from '../BitcoinWalletProvider.types';
import { NotBitcoinProviderError } from '../errors/NotBitcoinProviderError';
import { isBitcoinWalletProvider } from '../isBitcoinWalletProvider';

type SignMessageWithCustomOptionsParams = {
  addressType?: BitcoinAddressType;
  message: string;
  protocol?: BitcoinSignProtocol;
  walletAccount: BitcoinWalletAccount;
};

/**
 * Signs a message with custom options, like a specific address type or protocol
 *
 * If the wallet provider does not support specifying an address type or protocol, it will use the default values.
 *
 * @param params.addressType - The address type to use for signing ('ordinals' or 'payment')
 * @param params.message - The message to sign
 * @param params.protocol - The protocol to use for signing ('ecdsa' or 'bip322-simple')
 * @param params.walletAccount - The wallet account to sign the message with
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
 * @returns An object containing the signature
 * @throws {MethodNotImplementedError} If the wallet provider does not implement the signMessage method
 * @throws {NotBitcoinProviderError} If the wallet account is not a valid BitcoinWalletAccount
 */
export const signMessageWithCustomOptions = async (
  {
    walletAccount,
    message,
    protocol,
    addressType,
  }: SignMessageWithCustomOptionsParams,
  client = getDefaultClient()
): Promise<{ signature: string }> => {
  const provider = getWalletProviderFromWalletAccount(
    {
      walletAccount,
    },
    client
  );

  if (!isBitcoinWalletProvider(provider)) {
    throw new NotBitcoinProviderError({ providerKey: provider.key });
  }

  if (!provider.signMessage) {
    throw new MethodNotImplementedError('signMessage');
  }

  return provider.signMessage({
    addressType,
    message,
    protocol,
    walletAccount,
  });
};
